/**
 * JourneyContext (DC-1369)
 *
 * Provides journey identity (journeyId, journeyName) to all components
 * within the JourneyNavigator tree, eliminating prop drilling.
 *
 * Components can use the convenience hooks with an optional prop override:
 *   const journeyId = useJourneyId(props.journeyId);
 * This resolves prop-first, then context, enabling both patterns.
 */

import React, { createContext, useContext } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export interface JourneyContextValue {
  journeyId: string;
  journeyName: string;
}

// ============================================================================
// CONTEXT
// ============================================================================

const JourneyContext = createContext<JourneyContextValue | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface JourneyProviderProps {
  journeyId: string;
  journeyName: string;
  children: React.ReactNode;
}

export const JourneyProvider: React.FC<JourneyProviderProps> = ({
  journeyId,
  journeyName,
  children,
}) => (
  <JourneyContext.Provider value={{ journeyId, journeyName }}>
    {children}
  </JourneyContext.Provider>
);

// ============================================================================
// HOOKS
// ============================================================================

/** Get full journey context. Throws if called outside JourneyProvider. */
export function useJourney(): JourneyContextValue {
  const ctx = useContext(JourneyContext);
  if (!ctx) {
    throw new Error('useJourney must be used within a JourneyProvider');
  }
  return ctx;
}

/**
 * Get journeyId with optional prop override.
 * Resolves: propOverride > context > '' (empty string fallback).
 */
export function useJourneyId(propOverride?: string): string {
  const ctx = useContext(JourneyContext);
  return propOverride || ctx?.journeyId || '';
}

/**
 * Get journeyName with optional prop override.
 * Resolves: propOverride > context > '' (empty string fallback).
 */
export function useJourneyName(propOverride?: string): string {
  const ctx = useContext(JourneyContext);
  return propOverride || ctx?.journeyName || '';
}

export { JourneyContext };
